import {
  Container,
  styled,
  Box,
  Tooltip
} from "@mui/material"
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import { useState, useContext, useMemo } from "react";
import { useForm, Controller, useFieldArray, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import FormLabeledTextField from "components/form/FormLabeledTextField";
import FormSelect from "components/form/FormSelect";
import StyledButton from "components/button/StyledButton";
import { useNavigate } from "react-router-dom";
import { ProjectContext } from "context/ProjectContext";
import { useFormContext } from "context/FormContext";

import "../../components/button/StyledButton.css"
import "./AddProjectQuantities.css"

import FigmaModal from "components/modal/FigmaModal";
import { useCreateProjectMutation, useGetEcomLatestQuery, useGetMaterialCategoriesQuery, useGetMaterialTypesQuery, useGetUserQuery, useUpdateProjectMutation } from "api/api";
import { Project, Smqs } from "types/types";
import React from "react";
import SmqFields from "components/SmqFields/SmqFields";
import RunningCarbonTotal from "components/RunningCarbonTotal/RunningCarbonTotal";
import { useCreateUpdateProject } from "hooks/useCreateUpdateProject";
import { useStepBackNavigation } from "hooks/useStepBackNavigation";
import SaveErrorAlert from "components/form/SaveErrorAlert";
import RevertToDraftDialog from "components/form/RevertToDraftDialog";
import DiscardChangesDialog from "components/form/DiscardChangesDialog";
import { STEP_FIELDS } from "lib/utils/projectPatch";

const schema = z.object({
  smqs: z.array(
    z.object({
      material_type: z.object({
        category: z.string({
          required_error: "Category is required",
          invalid_type_error: "Category is required",
        }).min(1, "Category is required"),
        name: z.string({
          required_error: "Material type is required",
          invalid_type_error: "Material type is required",
        }).min(1, "Material type is required"),
      }),
      quantity: z.number({
        required_error: "Quantity is required",
        invalid_type_error: "Quantity is required",
      }).min(0.0000000000001, "Quantity must be a positive number"),
      a1_a3_carbon_factor_override: z
        .number({
          invalid_type_error: "Carbon factor override must be a positive number",
        })
        .nullable()
        .refine((value) => value === null || (value !== undefined && !isNaN(value) && value > 0), {
          message: "Carbon factor override must be a positive number",
        }),
    })
  ).optional(),
});

const columnWithTooltipStyles = { display: 'flex', alignItems: 'center', columnGap: '4px' };

const AddProjectSubstructureQuantities = () => {
  const navigate = useNavigate();

  const { data: latestEcomData = [] } = useGetEcomLatestQuery();
  const { data: materialCategories = [] } = useGetMaterialCategoriesQuery();
  const { data: materialTypes = [] } = useGetMaterialTypesQuery();
  const { data: userData } = useGetUserQuery();
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('ProjectContext error');
  }
  const { projectDetails } = context;

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      smqs: projectDetails.smqs || [],
    },
  });

  const {
    control,
    handleSubmit,
    getValues,
    trigger,
    formState: { isDirty, isSubmitting },
  } = form;

  const { handleCreateUpdateProject, error: saveError, isLoading, revertPrompt } = useCreateUpdateProject({ userData })

  const smqFieldArray = useFieldArray({
    control,
    name: "smqs",
  });

  // Use useWatch to observe changes in the smqs array
  const watchedSmqs = useWatch({
    control,
    name: "smqs",
  });

  const buildFormValues = () => ({ smqs: getValues().smqs });

  // Wait for the save and stay on the step if it failed - otherwise the user advances
  // believing the data was stored (ID 7).
  const handleGoToNextForm = async () => {
    const saved = await handleCreateUpdateProject({
      formValues: buildFormValues(),
      leaveForm: false,
      fields: STEP_FIELDS[3],
    });

    if (!saved) return;

    navigate('/add-project/4');
  }


  const handleSaveAndExit = async () => {
    handleCreateUpdateProject({ formValues: buildFormValues(), fields: STEP_FIELDS[3] })
  }

  const { handleClickBack, showDiscardPrompt, cancelDiscard, confirmDiscard } = useStepBackNavigation({
    previousPath: '/add-project/2',
    fields: STEP_FIELDS[3],
    buildFormValues,
    isDirty,
    trigger,
    save: handleCreateUpdateProject,
  });

  return (
    <StyledContainer>
      <div style={{ width: '100%', height: '90vh', background: 'white', boxShadow: '0px 1px 8px rgba(0, 0, 0, 0.12)', borderRadius: 5, padding: 20, paddingLeft: 41, paddingTop: 37 }}>
        <Box sx={{ width: "80vw", paddingLeft: ".5vw" }}>
          <div style={{ width: '100%', color: '#1A1A1A', fontSize: 28, fontFamily: 'Helvetica Neue LT Std', fontWeight: '300', wordWrap: 'break-word', paddingBottom: 12 }}>Substructure Quantities</div>
          <div className="form-container">
            <form onSubmit={handleSubmit(handleGoToNextForm)}>
              <div className="quantities-wrapper">
                <div className="header-row column-one row-title" style={{ alignContent: 'end', color: 'black' }}>Substructure</div>
                <div className="header-row column-two row-title">Material Category</div>
                <div className="header-row column-three row-title">Material Type</div>
                <div className="header-row column-four row-title">Quantity</div>
                <div className="header-row column-five row-title" style={columnWithTooltipStyles}>
                  A1-A3 Carbon Factor (Active: ECOM Version {latestEcomData["version"] ?? "default: ECOM v1.1"})
                  
                  <Tooltip
                    style={{ fontSize: 16 }} arrow placement="top"
                    title={'These factors are from industry-wide EPDs and may be overwritten if using product-specific EPDs or acceptable proxy data.  Refer to the ECOM tool on the SE 2050 website https://se2050.org for additional information on the carbon factors provided.'}
                  >
                    <HelpOutlineOutlinedIcon />
                  </Tooltip>
                </div>
                <div className="header-row column-six row-title" style={columnWithTooltipStyles}>
                  Lateral Element
                  
                  <Tooltip
                    style={{ fontSize: 16 }} arrow placement="top"
                    title={'Check this box if the structural component’s primary function is to resist lateral forces. Exclude components such as entire floor diaphragms if the primary function is to resist gravity loads.'}
                  >
                    <HelpOutlineOutlinedIcon />
                  </Tooltip>
                </div>
                <div className="header-row column-seven row-title" style={columnWithTooltipStyles}>
                  A1-A3 Embodied Carbon (ECOM)
                  
                  <Tooltip
                    style={{ fontSize: 16 }} arrow placement="top"
                    title={'Product of material quantity multiplied by A1-A3 Carbon Factor.'}
                  >
                    <HelpOutlineOutlinedIcon />
                  </Tooltip>
                </div>

                <SmqFields structure="Foundations" form={form} fieldArray={smqFieldArray} projectName={projectDetails.name} latestEcomData={latestEcomData} materialCategories={materialCategories} materialTypes={materialTypes} />
                <SmqFields structure="Foundation Walls" form={form} fieldArray={smqFieldArray} projectName={projectDetails.name} latestEcomData={latestEcomData} materialCategories={materialCategories} materialTypes={materialTypes} />
                <SmqFields structure="Slabs-On-Grade" form={form} fieldArray={smqFieldArray} projectName={projectDetails.name} latestEcomData={latestEcomData} materialCategories={materialCategories} materialTypes={materialTypes} />

              </div>
            </form>
          </div>
          <hr />
          <RunningCarbonTotal latestEcomData={latestEcomData} smqs={watchedSmqs || []} />
          <Box sx={{ display: "flex", mt: 3, justifyContent: "flex-start" }}>
            <StyledButton label="Save &amp; Exit" type="outlined" onClick={handleSubmit(handleSaveAndExit)}></StyledButton>
            <StyledButton label="Back" type="outlined" disabled={isSubmitting || isLoading} onClick={handleClickBack}></StyledButton>
            <StyledButton label={"Next"} type="filled" onClick={handleSubmit(handleGoToNextForm)}></StyledButton>
          </Box>
          <SaveErrorAlert error={saveError} />
          <RevertToDraftDialog {...revertPrompt} />
          <DiscardChangesDialog
            open={showDiscardPrompt}
            onCancel={cancelDiscard}
            onDiscard={confirmDiscard}
          />
          {/* </form> */}
        </Box>
      </div>
    </StyledContainer>
  );
}

const StyledContainer = styled(Container)({
  marginBottom: 24,
});


export default AddProjectSubstructureQuantities;